import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import theme from 'assets/styles/theme';
import {
  //COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  DEFAULT_UNPACK_ADJUSTMENT_ITEM_QTY,
  LS_DEFAULT_WAREHOUSE_KEY,
  LOADING_ICON_SIZE1,
} from 'constants/config';
import { SetDefaultWarehouseDialog } from 'components/common';
import Loading from 'components/common/Loading';
import { Label } from 'components/common/styles';
import { fetchWarehouseProductList } from 'services/inventory';
import { fetchWarehouseLocationList } from 'services/warehouse';
import { adjustPrepack } from 'services/unpack';
import { getWarehouseFromCache } from 'utils';


const AdjustmentWrapper = styled.div`
  margin: 20px 10px;

  & .act-btn {
    width: 100%;
  }

  & .bottom-btn-ctn {
    margin-top: 20px;
  }

  & .err-msg-ctn {
    color: ${theme['@danger-color']};
  }

  & .form-bottom-border {
    border-bottom: 1px solid #D9D9D9;
  }

  & .form-item-ctn {
    padding: 5px 0;
  }

  & .input-prd-qty-ctn .ant-input-number-group-wrapper {
    width: 100%;
  }

  & .label-ctn {
    margin-bottom: 5px;
  }

  & .input-ctn .ant-input-clear-icon {
    font-size: 24px;
  }
`;

const locationDict: StringKAnyVPair = {};

const UnpackAdjst = () => {
  const FORM_ELEMENT_SIZE = 'large';
  const { useState } = React;
  const [currentFromWarehouse, setCurrentFromWarehouse] = useState<StringKAnyVPair>({});
  const [defaultWarehouseVisible, setDefaultWarehouseVisible] = useState(false);
  const [fromLocation, setFromLocation] = useState<number>();
  //const [fromWarehouseList, setFromWarehouseList] = useState<StringKAnyVPair[]>();
  const [inited, setInited] = useState(false);
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [isLoadingWHLocation, setIsLoadingWHLocation] = useState(false);
  const [isSearchingSku, setIsSearchingSku] = useState(false);
  const [itemQty, setItemQty] = useState(1);
  const [skuInputVersion, setSkuInputVersion] = useState(0);
  const [skuItemList, setSkuItemList] = useState<StringKAnyVPair[]>([]);
  const [toLocation, setToLocation] = useState<number>();
  const [toLocationOptions, setToLocationOptions] = useState<any[]>([]);
  const [userError, setUserError] = useState<StringKAnyVPair>({});
  const [unpackAtSrcLocation, setUnpackAtSrcLocation] = useState(true);

  const skuSearchRef = React.useRef<any>(null);

  const history = useHistory();

  const adjust = async () => {
    if (isAdjusting) return;

    const skuItem = skuItemList.find(e => e.locationNum === fromLocation);
    const dl = toLocationOptions.find(e => e.value === toLocation);

    if (skuItem && dl) {
      const param = {
        WarehouseNum: currentFromWarehouse.warehouseNum,
        SKU: skuItem.sku,
        SourceLocationCode: skuItem.locationCode,
        SourceLocationNum: skuItem.locationNum,
        DestinationLocationCode: dl.label,
        DestinationLocationNum: dl.value,
        UnpackQty: itemQty,
      };

      //console.log(param, skuItem);
      setIsAdjusting(true);

      try {
        const res = await adjustPrepack(param);

        //console.log('r', res);
        if (res) {
          if (typeof res === 'object') {
            if (res.error) {
              Modal.error({
                centered: true,
                title: `Error: ${res.error}`,
              });
            }
          } else if (res === true) {
            Modal.confirm({
              cancelText: 'No',
              okText: 'Yes',
              centered: true,
              title: 'Adjusted successfully! Do you want to adjust another?',
              onCancel() {
                onClose();
              },
              onOk() {
                setSkuInputVersion(skuInputVersion + 1);
                clearForm();
                setTimeout(() => {
                  focusPrepackSkuSearchBox();
                }, 0);
              },
            });
          }
        } else {
          Modal.error({
            centered: true,
            title: `Data format error: ${res}`,
          });
        }
      } catch(e) {
        //console.log('e:', e);
        Modal.error({
          centered: true,
          title: `Error: ${e}`,
        });
      } finally {
        setIsAdjusting(false);
      }
    }
  };

  const clearForm = () => {
    //setSku('');
    setItemQty(1);
    setSkuItemList([]);
    setFromLocation(undefined);
    setToLocation(undefined);
  };

  const focusPrepackSkuSearchBox = () => {
    if (skuSearchRef.current) {
      skuSearchRef.current.focus();
    }
  };

  const fromLocationOptions = () => {
    const ld: StringKAnyVPair = {};
    const opts: StringKAnyVPair[] = [];

    skuItemList.forEach(e => {
      if (!ld[e.locationNum]) {
        ld[e.locationNum] = true;
        opts.push({
          label: e.locationCode,
          value: e.locationNum,
        });
      }
    });

    return opts;
  };

  const isAdjustQtyValid = () => {
    const skuItem = skuItemList.find(e => e.locationNum === fromLocation);

    return skuItem && itemQty <= skuItem.quantity;
  };

  const isDataInitialized = () => {
    return toLocationOptions.length > 0;
  };

  const isUnpackAvailable = () => {
    return skuItemList.length > 0 && fromLocation && toLocation && isAdjustQtyValid();
  };

  // eslint-disable-next-line
  const loadInitialData = async (wh = currentFromWarehouse) => {
    setIsLoadingWHLocation(true);

    try {
      if (!wh.warehouseId) {
        setIsLoadingWHLocation(false);
        return;
      }

      const locations = locationDict[wh.warehouseNum] ? locationDict[wh.warehouseNum] : await fetchWarehouseLocationList(wh.warehouseId, true);

      //console.log('wh', wh, locations);
      if (Array.isArray(locations)) {
        const opts = locations.map(e => {
          return {
            label: e.locationCode,
            value: e.locationNum,
          };
        });

        if (!locationDict[wh.warehouseNum]) {
          locationDict[wh.warehouseNum] = locations;
        }

        setToLocationOptions(opts);
      }
    } finally {
      setIsLoadingWHLocation(false);
      setTimeout(focusPrepackSkuSearchBox, 500);
    }
  };

  const maxAdjustQty = () => {
    const skuItem = skuItemList.find(e => e.locationNum === fromLocation);

    return skuItem ? skuItem.quantity : 1;
  };

  const onClose = () => {
    //props.onClose();
    history.push('/smart/unpack/');
  };

  const onCloseWarehouseSettingDialog = () => {
    setDefaultWarehouseVisible(false);
  };

  const onFromLocationChange = (val: any) => {
    const skuItem = skuItemList.find(e => e.locationNum === val);

    setFromLocation(val);
    setToLocation(val);
    setUserError({
      ...userError,
      qtyError: undefined,
      qtyErrMsg: undefined,
    });

    if (skuItem) {
      if (skuItem.quantity < itemQty) {
        //console.log(skuItem.quantity, itemQty, skuItem.locationCode);
        setItemQty(DEFAULT_UNPACK_ADJUSTMENT_ITEM_QTY);

        if (skuItem.quantity < DEFAULT_UNPACK_ADJUSTMENT_ITEM_QTY) {
          setUserError({
            ...userError,
            qtyError: true,
            qtyErrMsg: `Insufficient quantity of "${skuItem.sku}" to unpack`,
          });
        }
      }
    }
  };

  const onPrepackSkuSearchBoxKeyDown = (evt: any) => {
    //console.log(evt.charCode, evt.keyCode);
    if (evt.keyCode === 9) {
      if (evt.target.value) {
        const str = evt.target.value.trim();

        if (str && typeof str === 'string') {
          searchPrepackSKU(str);
        }
      }
    }
  };

  const onQtyChange = (val: any) => {
    setItemQty(val);
  };

  const onToLocationChange = (val: any) => {
    setToLocation(val);
  };

  const onUnpackSourceLocationChange = (chk: boolean) => {
    setUnpackAtSrcLocation(chk);

    if (chk) {
      setToLocation(fromLocation);
    }
  };

  const searchPrepackSKU = async (val: string, evt?: any) => {
    const sku = val.trim();

    if (isSearchingSku) return;

    if (sku) {
      //console.log(val, evt);
      const queryParam = {
        $top: 30,
        $sortBy: 'sku asc',
        $calculateTotal: true,
        $count: true,
        uniqueCode: sku,
        productStatus: 0,
        hideZero: true,
        thumbnailSize: 50
      };

      setIsSearchingSku(true);
      setUserError({});
      clearForm();

      try {
        const res = await fetchWarehouseProductList(currentFromWarehouse.warehouseId, queryParam);

        console.log('res', res);
        if (res && typeof res === 'object' && Array.isArray(res.data)) {
          if (res.data.length > 0) {
            const first = res.data[0];

            //setSku(sku);
            setSkuItemList([...res.data]);
            setFromLocation(first.locationNum);
            setToLocation(first.locationNum);
          } else {
            setUserError({
              skuError: true,
              skuErrMsg: `Insufficient inventory of "${sku}" to unpack`,
            });
          }
        }
      } catch(e) {
      } finally {
        setIsSearchingSku(false);
      }
    }
  };

  const submitButtonIcon = () => {
    return isAdjusting ? <Loading3QuartersOutlined spin /> : null;
  };

  React.useEffect(() => {
    if (!inited) {
      const whNum = parseInt(
        localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY) || '-1'
      );
      const wh = getWarehouseFromCache(whNum);

      if (wh) {
        //console.log('wh', wh);
        loadInitialData(wh);
        setInited(true);
        setCurrentFromWarehouse(wh);
      } else {
        setDefaultWarehouseVisible(true);
      }
    }
  }, [currentFromWarehouse, inited, loadInitialData]);

  return (<>
    {inited && (
      <AdjustmentWrapper>
        <div className="form-item-ctn form-bottom-border">
          <div>
            <Label className="label-bold tail-colon">Warehouse</Label>
            <Label className="label-grey">{currentFromWarehouse.warehouseCode}</Label>
          </div>
        </div>
        {isLoadingWHLocation && (
          <div className="form-item-ctn">
            <Loading size={LOADING_ICON_SIZE1} />
          </div>
        )}
        {isDataInitialized() && (<>
          <div className="form-item-ctn">
            <div className="label-ctn">
              <Label className="label-bold tail-colon">Prepack SKU</Label>
            </div>
            <div className="input-ctn">
              <Input.Search
                allowClear
                disabled={isAdjusting}
                enterButton
                key={`sku-input${skuInputVersion}`}
                loading={isSearchingSku}
                onKeyDown={onPrepackSkuSearchBoxKeyDown}
                onSearch={searchPrepackSKU}
                ref={skuSearchRef}
                size={FORM_ELEMENT_SIZE}
              />
            </div>
          </div>
          {userError.skuError && (
            <div className="form-item-ctn form-bottom-border">
              <div className="err-msg-ctn">{userError.skuErrMsg}</div>
            </div>
          )}
          {skuItemList.length > 0 && (<>
            <div className="form-item-ctn">
              <div className="label-ctn">
                <Label className="label-bold tail-colon">Qty</Label>
              </div>
              <div className="input-ctn input-prd-qty-ctn">
                <InputNumber
                  addonAfter={<Label className="label-grey">Max({maxAdjustQty()})</Label>}
                  disabled={isAdjusting || skuItemList.length === 0}
                  onChange={onQtyChange}
                  max={maxAdjustQty()}
                  min={1}
                  size={FORM_ELEMENT_SIZE}
                  value={itemQty}
                />
              </div>
            </div>
            {userError.qtyError && (
              <div className="form-item-ctn form-bottom-border">
                <div className="err-msg-ctn">{userError.qtyErrMsg}</div>
              </div>
            )}
            <div className="form-item-ctn">
              <div className="label-ctn">
                <Label className="label-bold tail-colon">From Location</Label>
              </div>
              <div className="input-ctn">
                <Select
                  disabled={isAdjusting || skuItemList.length === 0}
                  onChange={onFromLocationChange}
                  options={fromLocationOptions()}
                  size={FORM_ELEMENT_SIZE}
                  style={{ width: '100%' }}
                  value={fromLocation}
                />
              </div>
            </div>
            <div className="form-item-ctn">
              <Row align="middle" className="label-ctn">
                <Label className="label-bold tail-colon">Unpack at Source Location</Label>
                &nbsp;&nbsp;
                <Switch
                  disabled={isAdjusting || skuItemList.length === 0}
                  checked={unpackAtSrcLocation}
                  checkedChildren={'Yes'}
                  onChange={onUnpackSourceLocationChange}
                  unCheckedChildren={'No'}
                />
              </Row>
            </div>
            <div className="form-item-ctn">
              <div className="label-ctn">
                <Label className="label-bold tail-colon">To Location</Label>
              </div>
              <div className="input-ctn">
                <Select
                  disabled={skuItemList.length === 0 || unpackAtSrcLocation}
                  filterOption={(value: string, option: any)=> {return option.label.toLowerCase().indexOf(value.toLowerCase()) >= 0}}
                  onChange={onToLocationChange}
                  options={toLocationOptions}
                  showSearch
                  size={FORM_ELEMENT_SIZE}
                  style={{ width: '100%' }}
                  value={toLocation}
                />
              </div>
            </div>
            <Row className="form-item-ctn bottom-btn-ctn" justify="center">
              <Button
                className="act-btn"
                disabled={!isUnpackAvailable()}
                icon={submitButtonIcon()}
                onClick={adjust}
                size={FORM_ELEMENT_SIZE}
                type="primary"
              >
                Submit
              </Button>
            </Row>
          </>)}
        </>)}
      </AdjustmentWrapper>
    )}
    {defaultWarehouseVisible && (
      <SetDefaultWarehouseDialog
        closable={false}
        close={onCloseWarehouseSettingDialog}
        visible={defaultWarehouseVisible}
      />
    )}
  </>);
};

export default UnpackAdjst;
